import Link from "next/link";
import Meta from "@/components/meta";
import { GetStaticPaths, GetStaticProps } from "next";
import Navbar from "@/components/navbar";
import Footer from "@/components/footer";
import { getJobById, getJobSlugs, JobPosting } from "@/lib/jobsApi";

function JobPage({ job }: { job: JobPosting }) {
  const employmentType = job.title.toLowerCase().includes("contract")
    ? "Contract"
    : "Full-Time";
  return (
    <>
      <Meta
        title={job.title}
        name="Watershed Jobs"
        description={`Interested in joining Watershed? Learn about this ${job.department.toLowerCase()} role & others we’re hiring for.`}
      >
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "JobPosting",
              title: job.title,
              employmentType,
              jobLocation: job.location,
              description: `See more about this ${job.department} role on watershed.com.`,
              employmentUnit: job.department,
              url: `https://watershed.com/jobs/${job.slug}`,
              applicationContact: {
                "@type": "ContactPoint",
                email: "recruiting@watershed.com",
              },
              hiringOrganization: {
                "@type": "Organization",
                name: "Watershed",
                url: "https://watershed.com",
                logo: {
                  "@type": "ImageObject",
                  url: "https://watershed.com/favicon.svg",
                },
              },
            }),
          }}
        />
      </Meta>
      <Navbar />
      <main>
        <aside>
          <Link href="/jobs" passHref>
            <a
              style={{
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
              }}
            >
              <svg
                width="14"
                height="13"
                viewBox="0 0 14 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  d="M1 4H8.5C10.7091 4 12.5 5.79086 12.5 8V8C12.5 10.2091 10.7091 12 8.5 12H5M1 4L4.5 1.5M1 4L4.5 7"
                  stroke="#265CFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span style={{ marginLeft: 12 }}>All open roles</span>
            </a>
          </Link>
          <h1>{job.title}</h1>
          <dl>
            <div>
              <dt>Location</dt>
              <dd>{job.location.toString().replace(", United States", "")}</dd>
            </div>
            <div>
              <dt>Type</dt>
              <dd>{employmentType}</dd>
            </div>
            <div>
              <dt>Department</dt>
              <dd>{job.department}</dd>
            </div>
          </dl>
          <a
            href={job.greenhouseUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="button primary"
          >
            Apply now
          </a>
        </aside>
        <article className="blog-post">
          <div dangerouslySetInnerHTML={{ __html: job.content }} />
        </article>
        <a
          href={job.greenhouseUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="button primary mobile-only"
          style={{ width: "fit-content" }}
        >
          Apply now
        </a>
      </main>
      <Footer />
      <style jsx>{`
        main {
          display: grid;
          column-gap: 32px;
          row-gap: 48px;
          align-items: start;
        }

        h1 {
          margin: 0;
          line-height: 1.125;
        }

        aside {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
        }

        dl {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 16px;
          color: #494e5a;
        }
        dt {
          font-weight: bold;
        }

        article.blog-post {
          border: 0;
          padding: 0;
          border-radius: 0;
        }
        article.blog-post :global(div:first-child h2:first-child) {
          margin-top: 0;
        }

        @media (min-width: 820px) {
          main {
            grid-template-columns: 1fr 2fr;
            padding: 48px 12px;
          }

          aside {
            position: sticky;
            top: 108px;
          }

          article.blog-post {
            padding-top: 48px;
          }
          article.blog-post :global(h2) {
            font-size: 24px;
          }

          .mobile-only {
            display: none;
          }
        }

        @media (min-width: 1100px) {
          main {
            gap: 64px;
          }
        }
      `}</style>
    </>
  );
}

export default JobPage;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = await (
    await getJobSlugs()
  ).map((slug) => ({ params: { id: slug } }));
  return { paths, fallback: "blocking" };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params.id.toString().match(/^\d+/)?.[0];
  let job: JobPosting;
  try {
    job = await getJobById(id);
    if (!job?.greenhouseUrl) {
      return { notFound: true };
    }
  } catch (e) {
    return { notFound: true };
  }
  return { props: { job }, revalidate: 5 };
};
